import React, { Suspense } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { PlatformSiteAri } from '@atlassian/ari';

import type { UserProfilePreviewLoadState } from '../types';
import type { UserProfilePreviewRelayQuery } from './__generated__/UserProfilePreviewRelayQuery.graphql';
import { ErrorCard } from './components/ErrorCard';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { UserProfilePreviewRelayContent } from './UserProfilePreviewRelayContent';

export type UserProfilePreviewRelayProps = {
	accountId: string;
	/** Identifies the consuming experience to the recommendations service. */
	caller: string;
	/** Additional content shown with the resolved profile, hidden during loading or on error. */
	children?: React.ReactNode;
	cloudId: string;
	featureServiceModel: string;
	/** Reports a failure after the shared error card is committed. */
	onError?: React.ComponentProps<typeof ErrorBoundary>['onError'];
	onLinkClick?: React.ComponentProps<typeof UserProfilePreviewRelayContent>['onLinkClick'];
	/** Reports a committed profile and subsequent optional dataset status changes. */
	onLoad?: (state: UserProfilePreviewLoadState) => void;
	orgId: string;
	requestingUserId: string;
	sourceProduct?: string;
	testId?: string;
};

function buildSiteAri(cloudId: string, sourceProduct?: string): string {
	if (sourceProduct) {
		return `ari:cloud:${sourceProduct}::site/${cloudId}`;
	}
	return cloudId;
}

/**
 * Builds the collaboration-context ARI used to scope the recent-work (`workedOn`)
 * query to the origin site, so activity from other sites the person belongs to is
 * not returned. Returns `null` when the site ID is missing or invalid so the query
 * safely falls back to unscoped behaviour.
 */
function buildCollabContextId(cloudId: string): string | null {
	if (!cloudId) {
		return null;
	}
	try {
		return PlatformSiteAri.create({ siteId: cloudId }).toString();
	} catch {
		return null;
	}
}

/** Fetches a profile preview with the standard loading skeleton and error card. */
export function UserProfilePreviewRelay({
	onError,
	...props
}: UserProfilePreviewRelayProps): React.JSX.Element {
	return (
		<ErrorBoundary
			fallback={<ErrorCard />}
			onError={onError}
			resetKeys={[
				props.accountId,
				props.cloudId,
				props.orgId,
				props.requestingUserId,
				props.sourceProduct,
			]}
		>
			<Suspense key={props.accountId} fallback={<LoadingSkeleton />}>
				<UserProfilePreviewRelayQueryContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);
}

function UserProfilePreviewRelayQueryContent({
	accountId,
	children,
	cloudId,
	orgId,
	requestingUserId,
	sourceProduct,
	caller,
	featureServiceModel,
	testId,
	onLinkClick,
	onLoad,
}: Omit<UserProfilePreviewRelayProps, 'onError'>): React.JSX.Element {
	const accountIdARI = `ari:cloud:identity::user/${accountId}`;

	const data = useLazyLoadQuery<UserProfilePreviewRelayQuery>(
		graphql`
			query UserProfilePreviewRelayQuery(
				$first: Int!
				$filter: ActivityFilter
				$recommendationsQuery: SmartsRecommendationsQuery!
				$reportingLinesAaid: String!
				$reportingLinesOrgId: String!
				$reportingLinesSiteId: String!
				$singleUserAccountId: String!
				$singleUserRequestingUserId: String!
				$singleUserTenantId: String!
			) {
				activity @catch(to: RESULT) {
					workedOn(first: $first, filter: $filter) {
						...UserProfilePreviewRelayContent_workedOn
					}
				}
				smarts @catch(to: RESULT) {
					...UserProfilePreviewRelayContent_smarts
						@arguments(recommendationsQuery: $recommendationsQuery)
					recommendedUser(recommendationsQuery: $recommendationsQuery) @catch(to: RESULT) {
						__typename
					}
				}
				knowledgeDiscovery {
					...UserProfilePreviewRelayContent_user
						@arguments(
							accountId: $singleUserAccountId
							requestingUserId: $singleUserRequestingUserId
							tenantId: $singleUserTenantId
						)
					...UserProfilePreviewRelayContent_reportingLines
						@arguments(
							aaid: $reportingLinesAaid
							orgId: $reportingLinesOrgId
							siteId: $reportingLinesSiteId
						)
					reportingLines(
						aaid: $reportingLinesAaid
						orgId: $reportingLinesOrgId
						siteId: $reportingLinesSiteId
					) @catch(to: RESULT) @optIn(to: "KnowledgeDiscovery Get reporting lines") {
						__typename
						... on QueryError {
							__typename
						}
					}
				}
			}
		`,
		{
			first: 15,
			singleUserAccountId: accountId,
			singleUserRequestingUserId: requestingUserId,
			singleUserTenantId: cloudId,
			filter: { actors: [accountIdARI], collabContextId: buildCollabContextId(cloudId) },
			recommendationsQuery: {
				context: {
					userId: accountId,
					tenantId: cloudId,
				},
				modelRequestParams: {
					caller,
					experience: 'CgUserNearbyUser',
					featureServiceModel,
				},
				requestingUserId,
				maxNumberOfResults: 10,
			},
			reportingLinesAaid: accountId,
			reportingLinesOrgId: orgId,
			reportingLinesSiteId: buildSiteAri(cloudId, sourceProduct),
		},
	);
	const reportingLines = data.knowledgeDiscovery?.reportingLines;
	return (
		<>
			<UserProfilePreviewRelayContent
				onLoad={onLoad}
				userFragment={data.knowledgeDiscovery ?? undefined}
				workedOnFragment={
					data.activity?.ok ? (data.activity.value?.workedOn ?? undefined) : undefined
				}
				smartsFragment={data.smarts?.ok ? (data.smarts.value ?? undefined) : undefined}
				reportingLinesFragment={data.knowledgeDiscovery ?? undefined}
				collaboratorsFailed={
					data.smarts?.ok === false ||
					(data.smarts?.ok === true && data.smarts.value?.recommendedUser.ok === false)
				}
				recentWorkFailed={data.activity?.ok === false}
				reportingLinesFailed={
					reportingLines?.ok === false ||
					(reportingLines?.ok === true && reportingLines.value?.__typename === 'QueryError')
				}
				cloudId={cloudId}
				orgId={orgId}
				siteId={buildSiteAri(cloudId, sourceProduct)}
				requestingUserId={requestingUserId}
				testId={testId}
				onLinkClick={onLinkClick}
			/>
			{children}
		</>
	);
}
